import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, Play, Loader2 } from 'lucide-react';
import api, { getBackendRoot } from '../services/api';

const C = { black: '#000000', carbon: '#0D0D0D', carbonLight: '#161616', fire: '#FF5A00', bone: '#E8E0D0', ash: '#8A8A7A', subtle: '#2A2A2A', white: '#FFFFFF' };

const GlobalSearch = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/search', { params: { q: query, limit: 12 } });
        setResults(res.data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (r) => {
    setOpen(false);
    setQuery('');
    navigate(r.url);
  };

  const resolveImg = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${getBackendRoot()}${url}`;
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="global-search-btn"
        className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 rounded-lg text-zinc-400 hover:text-white transition-all text-sm">
        <Search size={15} />
        <span className="hidden sm:inline">Buscar...</span>
        <kbd className="hidden md:inline text-[10px] px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-500">⌘K</kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]" data-testid="global-search-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-xl mx-4" style={{ background: C.carbon, border: `1px solid ${C.subtle}` }}>
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: C.subtle }}>
          <Search size={18} style={{ color: C.ash }} />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Buscar en blog, videos, cursos..."
            data-testid="global-search-input"
            className="flex-1 bg-transparent outline-none text-white placeholder-zinc-500 text-sm" />
          {loading && <Loader2 size={16} className="animate-spin" style={{ color: C.fire }} />}
          <button onClick={() => setOpen(false)} className="p-1"><X size={16} style={{ color: C.ash }} /></button>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="max-h-[50vh] overflow-y-auto p-2">
            {results.map((r, i) => (
              <button key={i} onClick={() => handleSelect(r)} data-testid={`search-result-${i}`}
                className="w-full flex items-center gap-3 p-3 hover:bg-zinc-800/80 rounded-lg transition-colors text-left">
                {r.type === 'blog' ? (
                  <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${C.fire}15` }}>
                    {r.image ? <img src={resolveImg(r.image)} alt="" className="w-10 h-10 rounded object-cover" /> : <FileText size={16} style={{ color: C.fire }} />}
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0 bg-zinc-800">
                    <Play size={16} style={{ color: C.fire }} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{r.title}</p>
                  <p className="text-zinc-500 text-xs truncate">{r.type === 'blog' ? r.excerpt : r.subtitle || r.course}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded text-zinc-500 flex-shrink-0" style={{ border: `1px solid ${C.subtle}` }}>
                  {r.type === 'blog' ? 'Blog' : 'Video'}
                </span>
              </button>
            ))}
          </div>
        )}

        {query.length >= 2 && results.length === 0 && !loading && (
          <div className="p-8 text-center">
            <p className="text-zinc-500 text-sm">No se encontraron resultados para "{query}"</p>
          </div>
        )}

        {query.length < 2 && (
          <div className="p-6 text-center">
            <p className="text-zinc-600 text-xs">Escribe al menos 2 caracteres para buscar</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
